/**
 * AN INTERACTIVE RESEARCH SESSION — the machinery the stock analyst and the
 * mutual fund research analyst share. Each describes its backend in an `api`
 * object (the documents and the root field each answers under, see
 * stockAnalyst.js and mfAnalyst.js); this hook does the rest:
 *
 *   the session's state, the report's revisions and the one being read;
 *   the chat — each message is one subscription, streamed until DONE: the
 *     analyst's text, the tools it used, the revisions it wrote, the actions
 *     it proposes (each with a one-time confirmation token);
 *   approving or declining those actions (an `api` with a token document
 *     re-issues a token lost with a reload);
 *   the PDF download;
 *   closing — by hand at any time, and on its own AUTO_CLOSE_MS after the
 *     report is finalised.
 *
 * The backends keep the revisions and the proposed actions, not the
 * conversation, so the transcript is kept in this browser per session. Tokens
 * are kept in memory only.
 *
 * Mount it keyed by the session id: the transcript is read from storage when
 * the hook first renders and is not re-read for another id.
 */
import {
  useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore,
} from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { analystCallSnapshot, consumeAnalystCall, onAnalystCall } from '../analystVoice';
import { EMPTY } from './_shared';

/** How long a finalised session stays open for last questions before it closes itself. */
export const AUTO_CLOSE_MS = 10 * 60 * 1000;

/** Whether a session's status reads as closed. */
export const isClosedStatus = (status) => /clos|ended|expired|archiv/i.test(String(status || ''));

/** A JSON scalar as an object — it may arrive parsed or as a string. */
export function parseJson(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'object') return v;
  try { return JSON.parse(v); } catch { return v; }
}

/**
 * What a chat event is. Read from `type` and, where the type is not one this
 * knows, from the fields it carries — a revision event names its revision, a
 * proposed action its id — so a renamed type still lands.
 */
export function chatEventKind(ev) {
  const t = String(ev?.type || '').toUpperCase();
  if (t.includes('ERROR')) return 'error';
  if (t === 'DONE' || t.includes('COMPLETE') || t === 'END') return 'done';
  if (ev.pendingActionId != null || t.includes('ACTION') || t.includes('CONFIRM')) return 'action';
  if (ev.revisionId != null || t.includes('REVISION')) return 'revision';
  if (t.includes('TOOL') || (ev.toolName && !ev.text)) return /RESULT|END|DONE|OUTPUT/.test(t) ? 'toolResult' : 'tool';
  return 'text';
}
/** A token-by-token event is appended as is; a whole message starts a new paragraph. */
const isDelta = (ev) => /DELTA|TOKEN|CHUNK|STREAM/.test(String(ev?.type || '').toUpperCase());

function loadTranscript(key, id) {
  if (!id) return [];
  try {
    const list = JSON.parse(window.localStorage.getItem(`ki.${key}.chat.${id}`) || '[]');
    /* A turn that was streaming when the page went away ended with it. */
    return Array.isArray(list) ? list.map((m) => (m.streaming ? { ...m, streaming: false, stopped: true } : m)) : [];
  } catch {
    return [];
  }
}
function saveTranscript(key, id, list) {
  if (!id) return;
  try { window.localStorage.setItem(`ki.${key}.chat.${id}`, JSON.stringify(list.slice(-200))); } catch { /* storage off: the chat still works */ }
}

/* When this browser first saw the session final — the auto-close clock's
   start when the final revision carries no `lockedAt`. Kept so a reload does
   not restart it. */
function finalSeenAt(key, id) {
  try {
    const k = `ki.${key}.finalSeen.${id}`;
    const had = Number(window.localStorage.getItem(k));
    if (had) return had;
    const now = Date.now();
    window.localStorage.setItem(k, String(now));
    return now;
  } catch {
    return null;
  }
}

const isFinaliseAction = (a) => /final/i.test(String(a || ''));

export const refetchAll =(...queries) => Promise.all(queries.map((q) => q?.refetch?.().catch(() => null)));

export function useAnalystSession(sessionId, api) {
  const { key, docs, field } = api;
  const id = Number(sessionId) || null;
  const client = useApolloClient();
  const opts = { variables: { sessionId: id }, skip: !id, fetchPolicy: 'cache-and-network', errorPolicy: 'all' };
  const session = useQuery(docs.session, opts);
  const revisions = useQuery(docs.revisions, opts);
  const pending = useQuery(docs.pending, opts);

  const s = session.data?.[field.session] ?? null;
  const revList = revisions.data?.[field.revisions] ?? EMPTY;
  const latestId = s?.currentRevisionId ?? revList[revList.length - 1]?.id ?? null;

  /* The revision read: the reader's pick, else the latest — so a revision the
     analyst writes opens as it lands, unless an older one was picked. */
  const [picked, setPicked] = useState(null);
  const revisionId = picked ?? latestId;
  const [format, setFormat] = useState('markdown');
  const report = useQuery(docs.revision, {
    variables: { revisionId, format },
    skip: !revisionId,
    /* A revision never changes once written. */
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  /* ── the conversation ── */
  const [messages, setMessages] = useState(() => loadTranscript(key, id));
  useEffect(() => { saveTranscript(key, id, messages); }, [key, id, messages]);
  const [streaming, setStreaming] = useState(false);
  const sub = useRef(null);
  const tokens = useRef(new Map());
  const live = useRef({});
  live.current = { session, revisions, pending };
  useEffect(() => () => sub.current?.unsubscribe(), []);

  const patch = useCallback((msgId, fn) => setMessages((list) => list.map((m) => (m.id === msgId ? fn(m) : m))), []);
  /** Every read of the session again — after anything that changed it. */
  const reload = useCallback(() => {
    const { session: sq, revisions: rq, pending: pq } = live.current;
    return refetchAll(sq, rq, pq);
  }, []);

  /* `onAction(ev, data)` sees each proposal the turn makes; `onEnd()` runs once it ends. */
  const send = useCallback((text, { onAction, onEnd } = {}) => {
    const message = String(text ?? '').trim();
    if (!id || !message || sub.current) return false;
    const turn = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const agentId = `a-${turn}`;
    const at = new Date().toISOString();
    setMessages((list) => [
      ...list,
      { id: `u-${turn}`, role: 'user', text: message, at },
      { id: agentId, role: 'agent', text: '', steps: [], actions: [], revisions: [], streaming: true, at },
    ]);
    setStreaming(true);

    let ended = false;
    const end = () => {
      if (ended) return;
      ended = true;
      sub.current?.unsubscribe();
      sub.current = null;
      setStreaming(false);
      patch(agentId, (m) => ({ ...m, streaming: false }));
      reload();
      onEnd?.();
    };

    const handle = (ev) => {
      const kind = chatEventKind(ev);
      const { session: sq, revisions: rq, pending: pq } = live.current;
      if (kind === 'text' && ev.text) {
        patch(agentId, (m) => ({ ...m, text: m.text && !isDelta(ev) ? `${m.text}\n\n${ev.text}` : `${m.text}${ev.text}` }));
      } else if (kind === 'tool') {
        patch(agentId, (m) => ({ ...m, steps: [...m.steps, { name: ev.toolName || 'tool', done: false }] }));
      } else if (kind === 'toolResult') {
        patch(agentId, (m) => {
          const i = m.steps.map((x) => x.name).lastIndexOf(ev.toolName || 'tool');
          return i < 0 ? m : { ...m, steps: m.steps.map((x, j) => (j === i ? { ...x, done: true } : x)) };
        });
      } else if (kind === 'revision') {
        if (ev.revisionId != null) patch(agentId, (m) => ({ ...m, revisions: [...m.revisions, ev.revisionId] }));
        if (ev.text) patch(agentId, (m) => ({ ...m, text: m.text ? `${m.text}\n\n${ev.text}` : ev.text }));
        setPicked(null);
        refetchAll(sq, rq);
      } else if (kind === 'action') {
        if (ev.pendingActionId != null && ev.confirmationToken) tokens.current.set(Number(ev.pendingActionId), ev.confirmationToken);
        const data = parseJson(ev.data);
        onAction?.(ev, data);
        patch(agentId, (m) => ({
          ...m,
          actions: [...m.actions, {
            pendingActionId: ev.pendingActionId, preview: ev.preview, action: data?.action || ev.toolName || null, text: ev.text || null,
          }],
        }));
        refetchAll(pq);
      } else if (kind === 'error') {
        patch(agentId, (m) => ({ ...m, error: ev.text || 'The analyst could not answer.' }));
      } else if (kind === 'done') {
        end();
      }
    };

    sub.current = client
      .subscribe({ query: docs.chat, variables: { sessionId: id, message, ...(api.chatVars || {}) }, fetchPolicy: 'no-cache' })
      .subscribe({
        next: ({ data, errors }) => {
          if (errors?.length) patch(agentId, (m) => ({ ...m, error: errors.map((e) => e.message).join(' · ') }));
          const ev = data?.[field.chat];
          if (ev) handle(ev);
        },
        error: (err) => {
          patch(agentId, (m) => ({ ...m, error: err?.message || 'The connection to the analyst dropped.' }));
          end();
        },
        complete: end,
      });
    return true;
  }, [id, client, patch, reload, docs.chat, field.chat, api.chatVars]);

  /** Stops listening to the turn in flight; whatever the analyst already wrote stays. */
  const stop = useCallback(() => {
    if (!sub.current) return;
    sub.current.unsubscribe();
    sub.current = null;
    setStreaming(false);
    setMessages((list) => list.map((m) => (m.streaming ? { ...m, streaming: false, stopped: true } : m)));
  }, []);

  const clearTranscript = useCallback(() => setMessages([]), []);

  /* ── the voice call (api/analystVoice.js) ──
     What was said in this session's call joins the same conversation: each
     final line, and each `agent_event` envelope's analyst events —
     REPORT_UPDATED (re-read; the new revision opens), CONFIRMATION_REQUIRED
     (an approval card; nothing has run), ASSUMPTION_CONFLICTS (shown; nothing
     was applied). Every entry is taken once: the module keeps how far the
     screen has read, and a ref guards a remount reading the same snapshot. */
  const call = useSyncExternalStore(onAnalystCall, analystCallSnapshot);
  const ours = Boolean(api.voiceKind) && call.kind === api.voiceKind && Number(call.sessionId) === Number(id);
  const taken = useRef({ callId: null, seq: 0 });
  const agentSince = useRef(false);
  useEffect(() => {
    if (!ours) return;
    if (taken.current.callId !== call.callId) taken.current = { callId: call.callId, seq: 0 };
    const from = Math.max(call.consumed, taken.current.seq);
    const fresh = call.log.filter((e) => e.seq > from);
    if (!fresh.length) return;
    const last = fresh[fresh.length - 1].seq;
    taken.current.seq = last;
    consumeAnalystCall(last);

    const add = [];
    let refresh = false;
    let refreshPending = false;
    const agentMsg = (extra) => ({
      id: `v-${call.callId}-${add.length}-${Date.now()}`, role: 'agent', text: '', steps: [], actions: [], revisions: [], voice: true, at: new Date().toISOString(), ...extra,
    });
    fresh.forEach((e) => {
      if (e.type === 'line') {
        if (e.role === 'user') agentSince.current = false;
        else agentSince.current = true;
        add.push(e.role === 'user'
          ? { id: `v-${call.callId}-${e.seq}`, role: 'user', text: e.text, voice: !e.typed, at: e.at }
          : agentMsg({ id: `v-${call.callId}-${e.seq}`, text: e.text, at: e.at }));
        return;
      }
      const payload = e.payload || {};
      const events = Array.isArray(payload.data?.analyst_events) ? payload.data.analyst_events : [];
      /* The spoken reply is already in the transcript; the envelope's text is
         used only when nothing was heard — a reply that was never spoken. */
      if (payload.reply && !agentSince.current) {
        add.push(agentMsg({ text: String(payload.reply) }));
        agentSince.current = true;
      }
      events.forEach((ev) => {
        const type = String(ev?.type || '').toUpperCase();
        if (type === 'REPORT_UPDATED') {
          refresh = true;
          add.push(agentMsg({ text: '', revisions: ev.revision_id != null ? [ev.revision_id] : [] }));
        } else if (type === 'CONFIRMATION_REQUIRED') {
          refreshPending = true;
          add.push(agentMsg({ actions: [{ pendingActionId: ev.pending_action_id, preview: ev.preview, action: ev.action || ev.preview?.action || null }] }));
        } else if (type.includes('CONFLICT')) {
          add.push(agentMsg({ conflicts: ev.conflicts ?? ev, options: ev.options ?? null }));
        }
      });
    });
    if (add.length) setMessages((list) => [...list, ...add]);
    if (refresh) { setPicked(null); reload(); } else if (refreshPending) refetchAll(live.current.pending);
  }, [ours, call.callId, call.log, call.consumed, reload]);

  /* ── the analyst's proposed actions ── */
  const actions = pending.data?.[field.pending] ?? EMPTY;
  const [confirmAction] = useMutation(docs.confirm, { fetchPolicy: 'no-cache' });
  const [rejectAction] = useMutation(docs.reject, { fetchPolicy: 'no-cache' });
  /* A dummy document stands in where the backend issues no fresh tokens; it is never sent. */
  const [issueToken] = useMutation(docs.token ?? docs.confirm, { fetchPolicy: 'no-cache' });
  const [deciding, setDeciding] = useState(null);
  const decide = useCallback(async (pendingActionId, approve, note) => {
    const k = Number(pendingActionId);
    setDeciding(k);
    try {
      let outcome;
      if (approve) {
        let token = tokens.current.get(k);
        if (!token && docs.token) {
          const { data } = await issueToken({ variables: { sessionId: id, pendingActionId: k } });
          token = data?.[field.token]?.confirmationToken ?? null;
        }
        if (!token) throw new Error('This approval has lapsed with the page reload — ask the analyst to propose it again.');
        const { data } = await confirmAction({ variables: { sessionId: id, pendingActionId: k, confirmationToken: token } });
        outcome = data?.[field.confirm] ?? null;
      } else {
        const { data } = await rejectAction({ variables: { sessionId: id, pendingActionId: k, note: note || null } });
        outcome = data?.[field.reject] ?? null;
      }
      tokens.current.delete(k);
      await reload();
      return outcome;
    } finally {
      setDeciding(null);
    }
  }, [id, confirmAction, rejectAction, issueToken, reload, docs.token, field.token, field.confirm, field.reject]);
  /* With a token document every proposal can be approved; without, only those whose token is in hand. */
  const canApprove = useCallback((pendingActionId) => Boolean(docs.token) || tokens.current.has(Number(pendingActionId)), [docs.token]);

  /**
   * Finalise, by the reader's click rather than by asking the agent — the
   * same end as telling the analyst "finalise". There is no finalise mutation
   * of its own: the analyst's finalise proposal is what confirmStockAnalystAction
   * runs. So a finalise already waiting is approved at once; otherwise the
   * analyst is asked over the session's chat to propose it, and the proposal
   * that comes back is approved in the same click — no conversation, no
   * second step. Resolves the confirmation's outcome.
   */
  const [finalising, setFinalising] = useState(false);
  const finalise = useCallback(async () => {
    setFinalising(true);
    try {
      const waiting = actions.find((a) => isFinaliseAction(a.action) && /proposed|pending|required/i.test(a.status || 'proposed'));
      if (waiting) return await decide(waiting.id, true);
      const pendingActionId = await new Promise((resolve, reject) => {
        let got = null;
        const started = send(`Finalise the current revision${latestId ? ` (#${latestId})` : ''} of the report now, as it stands — propose the finalise action without further questions.`, {
          onAction: (ev, data) => {
            if (got == null && isFinaliseAction(data?.action || ev.toolName)) got = ev.pendingActionId;
          },
          onEnd: () => (got != null ? resolve(got) : reject(new Error('The analyst did not propose finalising — ask it in the chat what is missing.'))),
        });
        if (!started) reject(new Error('The analyst is still answering — try again when it has finished.'));
      });
      return await decide(pendingActionId, true);
    } finally {
      setFinalising(false);
    }
  }, [actions, decide, send, latestId]);

  /* ── the PDF ── */
  const [downloadReport, downloadState] = useMutation(docs.download, { fetchPolicy: 'no-cache' });
  const download = useCallback(async (revId) => {
    const { data } = await downloadReport({ variables: { revisionId: Number(revId) } });
    return data?.[field.download]?.url ?? null;
  }, [downloadReport, field.download]);

  /* ── closing ── */
  const closed = isClosedStatus(s?.status);
  const [closeSession, closeState] = useMutation(docs.close, {
    fetchPolicy: 'no-cache',
    refetchQueries: api.sessionsQuery ? [api.sessionsQuery] : [],
  });
  const close = useCallback(async () => {
    stop();
    const { data } = await closeSession({ variables: { sessionId: id } });
    await reload();
    return data?.[field.close] ?? null;
  }, [id, closeSession, stop, reload, field.close]);

  /* The auto-close clock starts when the report is finalised: the final
     revision's `lockedAt` where the backend gives one, else the moment this
     browser first saw it final. */
  const finalRevisionId = s?.finalRevisionId ?? null;
  const finalRev = useQuery(docs.revision, {
    variables: { revisionId: finalRevisionId, format: 'markdown' },
    skip: !finalRevisionId || closed,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const lockedAt = Date.parse(finalRev.data?.[field.revision]?.revision?.lockedAt || '') || null;
  const seenFinal = useRef(null);
  if (finalRevisionId && !lockedAt && !finalRev.loading && seenFinal.current == null) {
    seenFinal.current = finalSeenAt(key, id) ?? Date.now();
  }
  const finalisedAt = finalRevisionId ? (lockedAt ?? seenFinal.current) : null;
  const closesAt = !closed && finalisedAt ? finalisedAt + AUTO_CLOSE_MS : null;

  /* Closes once the clock runs out — never in the middle of a turn: it waits
     for the analyst to finish. Tried once; a failure leaves the Close button. */
  const [autoCloseError, setAutoCloseError] = useState(null);
  const autoTried = useRef(false);
  useEffect(() => {
    if (!closesAt || streaming || autoTried.current) return undefined;
    const t = setTimeout(() => {
      autoTried.current = true;
      close().catch((e) => setAutoCloseError(e));
    }, Math.max(0, closesAt - Date.now()));
    return () => clearTimeout(t);
  }, [closesAt, streaming, close]);

  const current = report.data?.[field.revision] ?? null;
  const inCall = ours && ['connecting', 'connected', 'reconnecting'].includes(call.status);
  const pickRevision = useCallback((rid) => setPicked(rid == null || rid === '' || Number(rid) === Number(latestId) ? null : Number(rid)), [latestId]);
  return useMemo(() => ({
    id,
    session: s,
    loading: session.loading && !s,
    error: session.error ?? null,
    reload,
    revisions: revList,
    revisionsLoading: revisions.loading,
    latestId,
    revisionId,
    pickRevision,
    format,
    setFormat,
    report: current,
    reportLoading: report.loading && !current,
    reportError: report.error ?? null,
    messages,
    streaming,
    send,
    stop,
    clearTranscript,
    actions,
    decide,
    deciding,
    canApprove,
    finalise,
    finalising,
    download,
    downloading: downloadState.loading,
    closed,
    close,
    closing: closeState.loading,
    closesAt,
    autoCloseError,
    /** A voice call is up (or coming up) for this session. */
    inCall,
  }), [
    id, s, session.loading, session.error, reload, revList, revisions.loading, latestId, revisionId, pickRevision, format, current,
    report.loading, report.error, messages, streaming, send, stop, clearTranscript, actions, decide, deciding, canApprove, finalise, finalising, download,
    downloadState.loading, closed, close, closeState.loading, closesAt, autoCloseError, inCall,
  ]);
}
